/**
 * Notification utility
 * 
 * This utility provides a unified interface for toast notifications,
 * using notistack (Snackbar) as the underlying implementation.
 * 
 * Usage:
 * import { notify } from '../utils/notification';
 * 
 * notify.success('Operation successful');
 * notify.error('Something went wrong');
 * notify.info('Please note this information');
 * notify.warning('Be careful with this action');
 */

import { enqueueSnackbar, closeSnackbar } from 'notistack';

// Default configuration for notifications
const DEFAULT_DURATION = 4000;
const DEFAULT_POSITION = 'bottom-right';

// Create the toast notification utility
const notify = {
  /**
   * Show a success notification
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  success: (message, options = {}) => {
    enqueueSnackbar(message, {
      variant: 'success',
      autoHideDuration: DEFAULT_DURATION,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      ...options
    });
  },

  /**
   * Show an error notification
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  error: (message, options = {}) => {
    enqueueSnackbar(message, {
      variant: 'error',
      autoHideDuration: DEFAULT_DURATION,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      ...options
    });
  },

  /**
   * Show an info notification
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  info: (message, options = {}) => {
    enqueueSnackbar(message, {
      variant: 'info',
      autoHideDuration: DEFAULT_DURATION,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      ...options
    });
  },

  /**
   * Show a warning notification
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  warning: (message, options = {}) => {
    enqueueSnackbar(message, {
      variant: 'warning',
      autoHideDuration: DEFAULT_DURATION,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      ...options
    });
  },

  /**
   * Close all currently open notifications
   */
  closeAll: () => {
    closeSnackbar();
  }
};

export { notify }; 